import React, { useState, useEffect } from 'react';

export default function Results() {
  const [allEvents, setAllEvents] = useState([]);
  const [resultsMap, setResultsMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGender, setSelectedGender] = useState('');

  // Fetch all data once
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, housesRes] = await Promise.all([
          fetch('/api/events'),
          fetch('/api/houses')
        ]);

        const events = await eventsRes.json();
        setAllEvents(events);

        // Fetch results for all events
        const resultMap = {};
        await Promise.all(
          events.map(async (ev) => {
            const res = await fetch(`/api/results/${ev.id}`);
            const results = await res.json();
            resultMap[ev.id] = Array.isArray(results) ? results : [];
          })
        );
        setResultsMap(resultMap);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load data:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper: extract gender from event name
  const getGender = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('boys')) return 'Boys';
    if (lower.includes('girls')) return 'Girls';
    return 'Mixed';
  };

  // Apply filters
  const filteredEvents = allEvents.filter((ev) => {
    const matchesSearch =
      searchTerm === '' ||
      ev.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resultsMap[ev.id] || []).some((r) =>
        r.student_name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory = !selectedCategory || ev.category === selectedCategory;
    const matchesGender = !selectedGender || getGender(ev.name) === selectedGender;

    return matchesSearch && matchesCategory && matchesGender;
  });

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading results...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>🏅 Event Results</h2>

      {/* Search & Filters */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search student or event..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            minWidth: '200px'
          }}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
        >
          <option value="">All Categories</option>
          <option value="LP Mini">LP Mini</option>
          <option value="LP Kiddies">LP Kiddies</option>
          <option value="UP Kiddies">UP Kiddies</option>
          <option value="HS Boys">HS Boys</option>
          <option value="HS Girls">HS Girls</option>
        </select>
        <select
          value={selectedGender}
          onChange={(e) => setSelectedGender(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
        >
          <option value="">All Genders</option>
          <option value="Boys">Boys</option>
          <option value="Girls">Girls</option>
        </select>
        {(searchTerm || selectedCategory || selectedGender) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('');
              setSelectedGender('');
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results List */}
      {filteredEvents.map((ev) => (
        <div
          key={ev.id}
          style={{
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            padding: '20px',
            margin: '20px 0',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {/* School & Event Header */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: '0', fontSize: '1.4rem', color: '#1e40af', fontWeight: 'bold' }}>
              AL MAQAR SCHOOL | BADARIYA NAGAR
            </h2>
            <h3 style={{ margin: '8px 0 0', fontSize: '1.2rem', color: '#dc2626', fontWeight: 'bold' }}>
              🏆 Athleon Sports Meet 2025–2026 🏆
            </h3>
            <h4 style={{ margin: '10px 0', fontSize: '1.1rem', color: '#1f2937' }}>
              {ev.name} <span style={{ color: '#6b7280', fontSize: '0.95rem' }}>({ev.category})</span>
            </h4>
          </div>

          {/* Results Table - Mobile Responsive */}
          {resultsMap[ev.id] && resultsMap[ev.id].length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #9ca3af', padding: '10px', backgroundColor: '#f3f4f6', fontWeight: 'bold', minWidth: '80px' }}>Position</th>
                    <th style={{ border: '1px solid #9ca3af', padding: '10px', backgroundColor: '#f3f4f6', fontWeight: 'bold', minWidth: '120px' }}>Student Name</th>
                    <th style={{ border: '1px solid #9ca3af', padding: '10px', backgroundColor: '#f3f4f6', fontWeight: 'bold', minWidth: '80px' }}>Class</th>
                    <th style={{ border: '1px solid #9ca3af', padding: '10px', backgroundColor: '#f3f4f6', fontWeight: 'bold', minWidth: '100px' }}>House</th>
                  </tr>
                </thead>
                <tbody>
                  {resultsMap[ev.id]
                    .sort((a, b) => a.position - b.position)
                    .map((r, idx) => (
                      <tr key={`${r.position}-${idx}`}>
                        <td style={{ border: '1px solid #d1d5db', padding: '10px', textAlign: 'center' }}>
                          {r.position === 1 ? '🥇 1st' : r.position === 2 ? '🥈 2nd' : r.position === 3 ? '🥉 3rd' : `${r.position}th`}
                        </td>
                        <td style={{ border: '1px solid #d1d5db', padding: '10px', fontWeight: '600', wordBreak: 'break-word' }}>{r.student_name}</td>
                        <td style={{ border: '1px solid #d1d5db', padding: '10px', wordBreak: 'break-word' }}>Class {r.class_name}</td>
                        <td style={{ border: '1px solid #d1d5db', padding: '10px', wordBreak: 'break-word' }}>{r.house_name}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', marginTop: '12px' }}>
              ⏳ No results yet
            </p>
          )}

          {/* Congratulations Footer */}
          {resultsMap[ev.id] && resultsMap[ev.id].length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <p style={{ margin: 0, fontSize: '1.1rem', color: '#16a34a', fontWeight: 'bold' }}>
                🎉 Congratulations to all participants! 🎉
              </p>
            </div>
          )}
        </div>
      ))}
      <footer style={{
          marginTop: '40px',
          padding: '16px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          color: '#6b7280',
          fontSize: '0.875rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          Designed and Developed by{' '}
          <a
            href="https://mcmedia.in"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            MCMedia
          </a>
        </footer>
    </div>
  );
}